<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router';
import { computed, toRef } from 'vue';
import {
  useRoute,
  useRouter,
} from 'vue-router';

import type { VRouterTab } from '@components/tabs/VRouterTabs/types';
import type { IStatusDescriptionOptions } from '@modules/affiliates-profile/composables';
import type {
  IAffiliateProfileFormSchema,
} from '@modules/affiliates-profile/types';
import VBackButton from '@components/buttons/VBackButton/VBackButton.vue';
import VButton from '@components/buttons/VButton/VButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import VLoader from '@components/loader/VLoader.vue';
import VRouterTabs from '@components/tabs/VRouterTabs/VRouterTabs.vue';
import { useAffiliateChangeStatus } from '@modules/affiliates-pending/composables';
import {
  AFFILIATES_PENDING_LIST_ROUTE_NAME,
  AFFILIATES_PENDING_PERMISSIONS,
  AFFILIATES_PENDING_PROFILE_ROUTE_NAME,
  TRANSLATION_GROUP_AFFILIATES_PENDING,
} from '@modules/affiliates-pending/constants';
import { useAffiliateProfileForms, useStatusDescription } from '@modules/affiliates-profile/composables';
import { TRANSLATION_GROUP_AFFILIATES_PROFILE } from '@modules/affiliates-profile/constants';
import { useAffiliatesProfileGetQuery } from '@modules/affiliates-profile/queries';
import {
  AFFILIATES_REQUEST_HISTORY_LIST_ROUTE_NAME,
  AFFILIATES_REQUEST_HISTORY_PROFILE_ROUTE_NAME,
} from '@modules/affiliates-request-history/constants';
import { EAasmState } from '@modules/core/enums';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { isString } from '@utils/guards';

const { getTranslation } = useI18n();
const route = useRoute();

const id = computed<number>(() => (
  isString(route.params.id) ? Number(route.params.id) : 0
));
const selectedTab = computed<string>(() => (
  isString(route.params.tab) ? route.params.tab : ''
));
const { data, isLoading } = useAffiliatesProfileGetQuery(id);

const { mainSchema, contactsSchema, offerSchema } = useAffiliateProfileForms(data, toRef(TRANSLATION_GROUP_AFFILIATES_PROFILE));

const affiliateStatusOptions = computed<IStatusDescriptionOptions>(() => ({
  date: data.value?.affiliate?.decision_time,
  name: data.value?.affiliate?.manager_who_approved_name,
  translationKey: TRANSLATION_GROUP_AFFILIATES_PROFILE,
  shortCode: 'MAIN_STATUS',
  dateProp: 'date',
  nameProp: 'name',
}));

const { description } = useStatusDescription(affiliateStatusOptions);

const router = useRouter();

const isPendingRoute = computed(() => route.name === AFFILIATES_PENDING_PROFILE_ROUTE_NAME);
const isAwaiting = computed(() => data.value?.affiliate?.aasm_state === EAasmState.AWAITING);

const backUrl = computed<RouteLocationRaw>(() => {
  if (isPendingRoute.value) {
    return {
      name: AFFILIATES_PENDING_LIST_ROUTE_NAME,
    };
  }

  return {
    name: AFFILIATES_REQUEST_HISTORY_LIST_ROUTE_NAME,
  };
});

const tabRouteName = computed(() => {
  if (isPendingRoute.value) {
    return AFFILIATES_PENDING_PROFILE_ROUTE_NAME;
  }

  return AFFILIATES_REQUEST_HISTORY_PROFILE_ROUTE_NAME;
});

const mainFormSchema = computed<IAffiliateProfileFormSchema>(() => ({
  ...mainSchema.value,
  aasm_state: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_AFFILIATES_PROFILE, 'MAIN_STATUS_LABEL').value,
    value: getTranslation(TRANSLATION_GROUP_AFFILIATES_PROFILE, `STATUS_${data.value?.affiliate?.aasm_state?.toUpperCase()}`).value,
    description: data.value?.affiliate?.manager_who_approved_name ? description.value : undefined,
  },
}));

const formSchema = computed<IAffiliateProfileFormSchema>(() => {
  switch (selectedTab.value) {
    case 'general':
      return mainFormSchema.value;
    case 'contacts':
      return contactsSchema.value;
    case 'offer':
      return offerSchema.value;
    default:
      throw new Error('Unknown tab');
  }
});

const tabs = computed<VRouterTab[]>(() => [
  {
    label: getTranslation(TRANSLATION_GROUP_AFFILIATES_PROFILE, 'MAIN_TAB').value,
    route: {
      name: tabRouteName.value,
      params: {
        id: route.params.id,
        tab: 'general',
      },
    },
  },
  {
    label: getTranslation(TRANSLATION_GROUP_AFFILIATES_PROFILE, 'CONTACT_TAB').value,
    route: {
      name: tabRouteName.value,
      params: {
        id: route.params.id,
        tab: 'contacts',
      },
    },
  },
  {
    label: getTranslation(TRANSLATION_GROUP_AFFILIATES_PROFILE, 'OFFER_TAB_NAME').value,
    route: {
      name: tabRouteName.value,
      params: {
        id: route.params.id,
        tab: 'offer',
      },
    },
  },
]);

const { deny, approve } = useAffiliateChangeStatus();

function onApprove(affId: number): void {
  approve(affId, undefined, saveCallback);
}

function onReject(affId: number): void {
  deny(affId, undefined, saveCallback);
}

function saveCallback(): void {
  void router.back(backUrl.value);
}
</script>

<template>
  <VCard v-data-qa-auto-id="'AffiliatesProfilePage'"
    type="form"
    :title="$getTranslation(TRANSLATION_GROUP_AFFILIATES_PROFILE, 'HEADER')"
  >
    <VRouterTabs
      :items="tabs"
      replace
      class="mb-10"
    />
    <VLoader
      :loading="isLoading"
      min-heigh="200px"
    >
      <VForm
        :schema="formSchema"
        readonly
      >
        <template #secondaryBtn>
          <VButton
            v-if="isPendingRoute && isAwaiting && $hasPermission(AFFILIATES_PENDING_PERMISSIONS.REQUEST)"
            :label="$getTranslation(TRANSLATION_GROUP_AFFILIATES_PENDING, 'ACCEPT_BTN')"
            severity="success"
            @click="onApprove(id)"
          />
          <VButton
            v-if="isPendingRoute && isAwaiting && $hasPermission(AFFILIATES_PENDING_PERMISSIONS.REQUEST)"
            :label="$getTranslation(TRANSLATION_GROUP_AFFILIATES_PENDING, 'REJECT_BTN')"
            severity="danger"
            @click="onReject(id)"
          />
          <VBackButton
            :to="backUrl"
            :label="$getTranslation(TRANSLATION_GROUP_AFFILIATES_PROFILE, 'BUTTON_BACK')"
          />
        </template>
      </VForm>
    </VLoader>
  </VCard>
</template>
